import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { BurnRequest, CommitPreflight, Schedule, SchedulePart, ScheduleFreq, SyncResult } from '../api/types';
import { SCHEDULE_FREQS } from '../api/types';
import { useAsync, usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatEUR, formatDate, formatDateTime, formatNumber, shortHash } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { SyncProgress } from '../components/SyncProgress';
import { Badge, Button, Card, CardHead, IdChip, Spinner, Stat } from '../components/ui';
import { RetentionBadge } from './shared';
import { IconArchive, IconBilling, IconDownload, IconVersions, IconRetention, IconRefresh, IconTrash } from '../components/Icons';

const PAGE = 25;

export function ArchiveDetail() {
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useNavigate();
  const toast = useToast();
  const { id = '' } = useParams();
  const detail = useAsync(() => api.getArchive(id), [id]);
  const objects = usePaginated((cursor) => api.listObjects(id, PAGE, cursor), [id]);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // The sync runs on the server, so what is shown here is a job's progress
  // rather than a local activity flag: it is picked up again after a reload
  // and shows a run somebody else started.
  const [syncJob, setSyncJob] = useState<SyncResult | null>(null);
  // The customer's most recent burn request: what they are waiting on, or the
  // reason an operator turned it down.
  const [burnRequest, setBurnRequest] = useState<BurnRequest | null>(null);
  // Remembers whether the last poll saw a running job, so the finish is
  // announced exactly once instead of on every subsequent poll.
  const wasRunning = useRef(false);
  const [burning, setBurning] = useState(false);
  const [snapRefresh, setSnapRefresh] = useState(0);
  // Whether a commit currently fits in the tenant's daily write budget. Purely
  // advisory — the server re-checks and answers 429 — but it lets us explain
  // WHY the button is unavailable instead of failing mid-action.
  const preflight = useAsync(() => api.commitPreflight(id), [id, snapRefresh]);
  const quota = useAsync(() => api.quota(), [snapRefresh]);
  const [confirmBurn, setConfirmBurn] = useState(false);
  const commitBlocked = preflight.data ? !preflight.data.allowed : false;

  // "Secure now" no longer seals anything: it files a request an operator has
  // to approve. A burn consumes write-once medium and is billed, so it is not
  // a customer's click to complete on its own.
  const doBurn = async () => {
    setBurning(true);
    try {
      const req = await api.requestBurn(id);
      setBurnRequest(req);
      toast.success(t('snapshots.requested'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t('snapshots.rateLimited'));
      } else if (err instanceof ApiError && err.status === 409) {
        // Already queued — show that rather than an error for something that
        // is, from the customer's point of view, in hand.
        setBurnRequest(err.body as BurnRequest);
        toast.info(t('snapshots.alreadyRequested'));
      } else {
        toast.error(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setBurning(false);
      setConfirmBurn(false);
      preflight.reload();
      quota.reload();
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteArchive(id);
      toast.success(t('archives.deleted'));
      navigate('/archives');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Announces the outcome once and refreshes what the sync changed.
  const announce = useCallback(
    (job: SyncResult) => {
      if (job.status === 'completed_partial') {
        // A truncated run used to report plain success, which is how three 2 GB
        // files sat unsynchronised while the portal kept saying "completed".
        toast.warning(
          t('archives.syncedPartial', {
            count: job.imported,
            skipped: job.skipped ?? 0,
            limit: formatBytes(job.limitBytes ?? 0, lang),
          }),
        );
      } else if (job.status === 'failed') {
        toast.error(job.error ?? t('archives.syncFailed'));
      } else {
        toast.success(t('archives.synced', { count: job.imported }));
      }
      detail.reload();
      objects.reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, t, toast],
  );

  // Poll while a job is running. Also runs on mount, so navigating back to a
  // long sync shows it in progress instead of nothing at all.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const job = await api.syncStatus(id);
        if (cancelled) return;
        setSyncJob(job ?? null);
        if (job && !job.done) {
          wasRunning.current = true;
          setSyncing(true);
          timer = setTimeout(tick, 2000);
          return;
        }
        setSyncing(false);
        if (job && wasRunning.current) {
          wasRunning.current = false;
          announce(job);
        }
      } catch {
        // A failed poll must not kill the loop; the next one may well succeed.
        if (!cancelled) timer = setTimeout(tick, 5000);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id, announce]);

  // Follow the request until it is settled: an approval builds the DMD in the
  // background, so the outcome arrives minutes after the operator clicked.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const settled = (s: string) => s === 'completed' || s === 'rejected' || s === 'failed';

    const tick = async () => {
      try {
        const res = await api.burnRequests(id);
        if (cancelled) return;
        const latest = res.items[0] ?? null;
        setBurnRequest(latest);
        if (latest && !settled(latest.status)) {
          timer = setTimeout(tick, 5000);
        } else if (latest?.status === 'completed') {
          setSnapRefresh((n) => n + 1);
          quota.reload();
        }
      } catch {
        if (!cancelled) timer = setTimeout(tick, 10000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const job = await api.syncArchive(id);
      wasRunning.current = true;
      setSyncJob(job);
    } catch (err) {
      // 409 means a sync is already under way — adopt it rather than reporting
      // an error for something that is, from the operator's view, working.
      if (err instanceof ApiError && err.status === 409) {
        wasRunning.current = true;
        setSyncJob(err.body as SyncResult);
        return;
      }
      toast.error(err instanceof ApiError ? err.message : String(err));
      setSyncing(false);
    }
  };

  return (
    <ApiState {...detail} onRetry={detail.reload}>
      {(d) => {
        const a = d.archive;
        return (
          <>
            <PageHeader
              title={a.name}
              subtitle={t('archives.detailTitle')}
              actions={
                <>
                  {d.source ? (
                    <Button variant="secondary" onClick={doSync} disabled={syncing} title={t('archives.syncHint')}>
                      {syncing ? <Spinner /> : <IconRefresh width={18} height={18} />}
                      {t('archives.sync')}
                    </Button>
                  ) : null}
                  <Link className="btn btn--secondary" to={`/retention?archive=${a.id}`}>
                    <IconRetention width={18} height={18} />
                    {t('retention.change')}
                  </Link>
                  <Button
                    variant="primary"
                    onClick={() => setConfirmBurn(true)}
                    disabled={burning || commitBlocked}
                    title={commitBlocked ? t('snapshots.blockedHint') : t('snapshots.burnHint')}
                  >
                    {burning ? <Spinner /> : <IconArchive width={18} height={18} />}
                    {t('snapshots.burn')}
                  </Button>
                  <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                    <IconTrash width={18} height={18} />
                    {t('archives.delete')}
                  </Button>
                </>
              }
            />

            <div className="stack">
              {/* Shown whenever a job exists — running or just finished — so a
                  long sync is visible on return and its outcome is not lost
                  with a dismissed toast. */}
              {syncJob ? (
                <Card>
                  <CardHead title={t('archives.syncTitle')} />
                  <div className="card__body">
                    <SyncProgress job={syncJob} lang={lang} />
                  </div>
                </Card>
              ) : null}

              {/* What the customer is waiting on. Shown for a decided request
                  too, because a rejection carries the operator's reason and
                  that must not vanish with a dismissed toast. */}
              {burnRequest && burnRequest.status !== 'completed' ? (
                <Card>
                  <CardHead title={t('snapshots.requestTitle')} />
                  <div className="card__body">
                    <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Badge
                        tone={
                          burnRequest.status === 'rejected' || burnRequest.status === 'failed'
                            ? 'danger'
                            : burnRequest.status === 'building'
                              ? 'info'
                              : 'warning'
                        }
                      >
                        {t(`snapshots.requestState.${burnRequest.status}`, burnRequest.status)}
                      </Badge>
                      <span className="muted" style={{ fontSize: 13 }}>
                        {t('snapshots.requestedAt', {
                          when: formatDateTime(burnRequest.requestedAt, lang),
                          volume: formatBytes(burnRequest.estBytes, lang),
                          cost: formatEUR(burnRequest.estCostEur, lang),
                        })}
                      </span>
                    </div>
                    {burnRequest.note ? (
                      <p style={{ fontSize: 13, margin: '8px 0 0' }}>
                        <strong>{t('snapshots.requestNote')}:</strong> {burnRequest.note}
                      </p>
                    ) : null}
                    {burnRequest.error ? (
                      <p style={{ fontSize: 13, margin: '8px 0 0', color: 'var(--danger)' }} role="alert">
                        {burnRequest.error}
                      </p>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              <div className="grid grid--stats">
                <Stat label={t('archives.colObjects')} value={formatNumber(a.objects, lang)} icon={<IconArchive width={16} height={16} />} />
                <Stat label={t('archives.colSize')} value={formatBytes(a.bytes, lang)} icon={<IconBilling width={16} height={16} />} />
                <div className="card stat">
                  <div className="stat__label">{t('archives.colRetention')}</div>
                  <div style={{ marginTop: 10 }}>
                    <RetentionBadge policy={a.retentionPolicy} />
                  </div>
                </div>
                <div className="card stat">
                  <div className="stat__label">{t('archives.identifier')}</div>
                  <div style={{ marginTop: 10 }}>
                    <IdChip id={a.id} />
                  </div>
                </div>
              </div>

              <Card>
                <CardHead title={t('archives.objectsTitle')} />
                <ApiState
                  status={objects.status}
                  error={objects.error}
                  data={objects.status === 'error' ? null : objects.items}
                  onRetry={objects.reload}
                >
                  {(items) =>
                    items.length === 0 ? (
                      <EmptyState title={t('archives.objectsEmpty')} />
                    ) : (
                      <>
                        <div className="table-wrap">
                          <table className="data">
                            <thead>
                              <tr>
                                <th>{t('archives.colPath')}</th>
                                <th className="num">{t('archives.colSize')}</th>
                                <th className="num">{t('archives.colVersions')}</th>
                                <th>{t('archives.colHash')}</th>
                                <th>{t('common.createdAt')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((o) => (
                                <tr key={o.id}>
                                  <td className="cell-strong">{o.path}</td>
                                  <td className="num">{formatBytes(o.bytes, lang)}</td>
                                  <td className="num">
                                    <Link to={`/versions?archive=${a.id}&object=${o.id}`}>
                                      <IconVersions width={14} height={14} /> {formatNumber(o.versions, lang)}
                                    </Link>
                                  </td>
                                  <td>
                                    <span className="mono" title={o.sha256}>
                                      {shortHash(o.sha256)}
                                    </span>
                                  </td>
                                  <td className="muted">{formatDate(o.createdAt, lang)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <LoadMore hasMore={objects.hasMore} loading={objects.loadingMore} onLoadMore={objects.loadMore} />
                      </>
                    )
                  }
                </ApiState>
              </Card>

              <SnapshotHistory archiveId={a.id} refreshKey={snapRefresh} preflight={preflight.data} />
              <ScheduleCard archiveId={a.id} hasSource={Boolean(d.source)} />
            </div>

            <ConfirmDialog
              open={confirmBurn}
              busy={burning}
              title={t('snapshots.confirmTitle')}
              confirmLabel={t('snapshots.burn')}
              onConfirm={doBurn}
              onCancel={() => setConfirmBurn(false)}
              body={
                <div className="stack" style={{ gap: 8 }}>
                  <p style={{ margin: 0 }}>{t('snapshots.confirmBody', { name: a.name })}</p>
                  <table className="data">
                    <tbody>
                      <tr>
                        <td>{t('snapshots.confirmVolume')}</td>
                        <td className="num cell-strong">
                          {formatBytes(preflight.data?.estimatedBytes ?? a.bytes, lang)}
                        </td>
                      </tr>
                      {preflight.data && preflight.data.estimatedCostEur > 0 ? (
                        <tr>
                          <td>{t('snapshots.confirmCost')}</td>
                          <td className="num cell-strong">
                            {formatEUR(preflight.data.estimatedCostEur, lang)}
                          </td>
                        </tr>
                      ) : null}
                      {quota.data?.hasQuota ? (
                        <tr>
                          <td>{t('snapshots.confirmQuota')}</td>
                          <td className="num">
                            {formatBytes(quota.data.remainingBytes, lang)}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                  {preflight.data && preflight.data.estimatedCostEur === 0 ? (
                    <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                      {t('snapshots.confirmNoPrice')}
                    </p>
                  ) : null}
                </div>
              }
            />

            <ConfirmDialog
              open={confirmDelete}
              danger
              busy={deleting}
              title={t('archives.deleteConfirmTitle')}
              body={t('archives.deleteConfirmBody', { name: a.name })}
              confirmLabel={t('archives.delete')}
              onConfirm={doDelete}
              onCancel={() => setConfirmDelete(false)}
            />
          </>
        );
      }}
    </ApiState>
  );
}

// Snapshot history: every sealed/burned DMD image of the archive, each
// retrievable via its retained blob.
function SnapshotHistory({
  archiveId,
  refreshKey,
  preflight,
}: {
  archiveId: string;
  refreshKey: number;
  /** Remaining daily write budget, so the header can show it. */
  preflight: CommitPreflight | null;
}) {
  const { t } = useTranslation();
  const lang = useLang();
  const snaps = useAsync(() => api.listSnapshots(archiveId), [archiveId, refreshKey]);

  return (
    <Card>
      <CardHead title={t('snapshots.title')}>
        {preflight && preflight.estimatedCostEur > 0 ? (
          <Badge tone="info">
            {t('snapshots.quote', { cost: formatEUR(preflight.estimatedCostEur, lang) })}
          </Badge>
        ) : null}
        {preflight?.hasLimit ? (
          <Badge tone={preflight.allowed ? 'default' : 'danger'}>
            {preflight.allowed
              ? t('snapshots.budgetLeft', { left: formatBytes(preflight.remainingBytes, lang) })
              : t('snapshots.budgetExceeded', {
                  need: formatBytes(preflight.estimatedBytes, lang),
                  left: formatBytes(preflight.remainingBytes, lang),
                })}
          </Badge>
        ) : null}
      </CardHead>
      <ApiState {...snaps} onRetry={snaps.reload}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState title={t('snapshots.empty')} icon={<IconArchive width={26} height={26} />} />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="num">{t('snapshots.colSeq')}</th>
                    <th>{t('snapshots.colTrigger')}</th>
                    <th className="num">{t('archives.colObjects')}</th>
                    <th className="num">{t('snapshots.colDmdSize')}</th>
                    <th>{t('archives.colHash')}</th>
                    <th>{t('common.createdAt')}</th>
                    <th className="num">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((s) => (
                    <tr key={s.id}>
                      <td className="num cell-strong">v{s.seq}</td>
                      <td>
                        {s.trigger === 'scheduled' ? (
                          <Badge tone="accent">{t('snapshots.triggerScheduled')}</Badge>
                        ) : (
                          <Badge>{t('snapshots.triggerManual')}</Badge>
                        )}
                      </td>
                      <td className="num">{formatNumber(s.objectCount, lang)}</td>
                      <td className="num">{formatBytes(s.dmdSize, lang)}</td>
                      <td>
                        <span className="mono" title={s.sha256}>{shortHash(s.sha256)}</span>
                      </td>
                      <td className="muted">{formatDateTime(s.createdAt, lang)}</td>
                      <td className="num">
                        <a className="btn btn--secondary btn--sm" href={api.snapshotDownloadUrl(archiveId, s.id)}>
                          <IconDownload width={16} height={16} />
                          {t('snapshots.retrieve')}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </ApiState>
    </Card>
  );
}

// Schedule: automated daily/weekly/monthly sync (re-pull source) and commit
// (burn a snapshot).
function ScheduleCard({ archiveId, hasSource }: { archiveId: string; hasSource: boolean }) {
  const { t } = useTranslation();
  const sched = useAsync(() => api.getSchedule(archiveId), [archiveId]);
  return (
    <Card>
      <CardHead title={t('schedule.title')} />
      <ApiState {...sched} onRetry={sched.reload}>
        {(data) => <ScheduleForm archiveId={archiveId} hasSource={hasSource} initial={data} />}
      </ApiState>
    </Card>
  );
}

const pad2 = (n: number) => String(n).padStart(2, '0');

// SchedulePartControls renders freq + (weekday | day-of-month) + time for one
// scheduled action.
function SchedulePartControls({
  freqLabel,
  freqHint,
  part,
  onChange,
}: {
  freqLabel: string;
  freqHint: string;
  part: SchedulePart;
  onChange: (p: SchedulePart) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="stack" style={{ gap: 10 }}>
      <Field label={freqLabel} hint={freqHint}>
        {(p) => (
          <select className="select" {...p} value={part.freq} onChange={(e) => onChange({ ...part, freq: e.target.value as ScheduleFreq })}>
            {SCHEDULE_FREQS.map((f) => (
              <option key={f} value={f}>{t(`schedule.freq_${f}`)}</option>
            ))}
          </select>
        )}
      </Field>
      {part.freq === 'weekly' ? (
        <Field label={t('schedule.weekday')}>
          {(p) => (
            <select className="select" {...p} value={part.weekday} onChange={(e) => onChange({ ...part, weekday: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <option key={d} value={d}>{t(`schedule.wd_${d}`)}</option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {part.freq === 'monthly' ? (
        <Field label={t('schedule.dayOfMonth')}>
          {(p) => (
            <select className="select" {...p} value={part.dom} onChange={(e) => onChange({ ...part, dom: Number(e.target.value) })}>
              <option value={0}>{t('schedule.domLast')}</option>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{t('schedule.domNth', { n: d })}</option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {part.freq !== 'off' ? (
        <Field label={t('schedule.time')} hint={t('schedule.tz')}>
          {(p) => (
            <input
              className="input"
              type="time"
              {...p}
              value={`${pad2(part.hour)}:${pad2(part.minute)}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':');
                onChange({ ...part, hour: Number(h) || 0, minute: Number(m) || 0 });
              }}
            />
          )}
        </Field>
      ) : null}
    </div>
  );
}

function ScheduleForm({ archiveId, hasSource, initial }: { archiveId: string; hasSource: boolean; initial: Schedule }) {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [sync, setSync] = useState<SchedulePart>({
    freq: initial.syncFreq, hour: initial.syncHour, minute: initial.syncMinute, weekday: initial.syncWeekday, dom: initial.syncDom,
  });
  const [commit, setCommit] = useState<SchedulePart>({
    freq: initial.commitFreq, hour: initial.commitHour, minute: initial.commitMinute, weekday: initial.commitWeekday, dom: initial.commitDom,
  });
  const [current, setCurrent] = useState<Schedule>(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.setSchedule(archiveId, {
        syncFreq: sync.freq, syncHour: sync.hour, syncMinute: sync.minute, syncWeekday: sync.weekday, syncDom: sync.dom,
        commitFreq: commit.freq, commitHour: commit.hour, commitMinute: commit.minute, commitWeekday: commit.weekday, commitDom: commit.dom,
      });
      setCurrent(r);
      toast.success(t('schedule.saved'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card__body">
      <div className="grid grid--2" style={{ alignItems: 'start' }}>
        {hasSource ? (
          <SchedulePartControls freqLabel={t('schedule.sync')} freqHint={t('schedule.syncHint')} part={sync} onChange={setSync} />
        ) : null}
        <SchedulePartControls freqLabel={t('schedule.commit')} freqHint={t('schedule.commitHint')} part={commit} onChange={setCommit} />
      </div>

      {(current.commitFreq !== 'off' && current.commitNext) || (hasSource && current.syncFreq !== 'off' && current.syncNext) ? (
        <div className="alert alert--info" style={{ marginTop: 14, marginBottom: 0 }}>
          <IconRetention width={18} height={18} />
          <span>
            {current.commitFreq !== 'off' && current.commitNext ? t('schedule.nextCommit', { when: formatDateTime(current.commitNext, lang) }) : null}
            {hasSource && current.syncFreq !== 'off' && current.syncNext ? ` · ${t('schedule.nextSync', { when: formatDateTime(current.syncNext, lang) })}` : null}
          </span>
        </div>
      ) : null}

      <Button variant="primary" onClick={save} disabled={busy} style={{ marginTop: 14 }}>
        {busy ? <Spinner /> : null}
        {t('schedule.save')}
      </Button>
    </div>
  );
}
